import { Button, Icons } from '@dbrain/ui';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMe } from '../../api/hooks.js';
import { isSuperAdmin, useLicense } from '../../api/license.js';
import { LicenseUploadDialog } from './LicenseUploadDialog.js';

/**
 * The read-only card (LisansSaltOkunur, D-124, D-126) in place of the message box, in a conversation and on the
 * home page of both products: what still works and that nothing was deleted (after the free trial: that it ran
 * out, D-133); super admins upload a license from here, the others are told to tell theirs; a conversation can
 * be exported. `extra`: a run still going may be stopped.
 */
export function ReadOnlyCard({ onExport, extra }: { onExport?: () => void; extra?: ReactNode }) {
  const { t } = useTranslation();
  const me = useMe().data;
  const license = useLicense().data;
  const [upload, setUpload] = useState(false);
  const titleId = useId();
  const admin = isSuperAdmin(me);
  return (
    <section className="read-only-card" aria-labelledby={titleId} data-testid="read-only-card">
      <span className="read-only-card__icon" aria-hidden="true">
        <Icons.lock size={18} />
      </span>
      <div className="read-only-card__text">
        <h2 id={titleId} className="read-only-card__title">
          {t('license.readOnly.title')}
        </h2>
        <p>{t(license?.basis === 'trial' ? 'license.readOnly.bodyTrial' : license?.endsOn ? 'license.readOnly.body' : 'license.readOnly.bodyNone')}</p>
        <div className="read-only-card__actions">
          {admin ? (
            <Button icon={<Icons.upload size={16} />} onClick={() => setUpload(true)} data-testid="read-only-upload">
              {t('license.upload.title')}
            </Button>
          ) : (
            <span className="read-only-card__tell" data-testid="read-only-tell-admin">
              <Icons.info size={16} />
              {t('license.readOnly.tellAdmin')}
            </span>
          )}
          {onExport ? (
            <Button variant="secondary" icon={<Icons.download size={16} />} onClick={onExport} data-testid="read-only-export">
              {t('license.readOnly.export')}
            </Button>
          ) : null}
          {extra}
        </div>
      </div>
      {upload ? <LicenseUploadDialog onClose={() => setUpload(false)} /> : null}
    </section>
  );
}
